import React from 'react';
import {Dialog, DialogProps} from 'primereact/dialog';
import {createGlobalStyle} from "styled-components";
import {FileUpload, FileUploadProps} from "primereact/fileupload";
import {clsx} from "clsx";
import {CustomErrorMessage} from "@/Messages/Toast/General/CustomErrorMessage";
import {useQueryClient} from "@tanstack/react-query";
import {useToastMessagesStore} from "@/Stores/ToastMessagesStore";
import {confirmPopup, ConfirmPopup} from "primereact/confirmpopup";
import {QueryKeys} from "@/Service/Api/V1/QueryKeys/QueryKeys";
import ErrorsBlock from "@/Components/Forms/Form/ErrorsBlock";
import {useUpdateUserGuide} from "@/Service/Api/V1/ApiHooks/Misc/useUpdateUserGuide";

export type UploadUserGuideModalProps = {
    dialogProps: DialogProps,
    fileUploadProps?: FileUploadProps,
};

const UploadUserGuideModalStyle = createGlobalStyle`
    .update-user-guide-modal {
        width: 50%;
    }
`;

const UploadUserGuideModal: React.FC<UploadUserGuideModalProps> = ({
                                                                       dialogProps,
                                                                       fileUploadProps
                                                                   }: UploadUserGuideModalProps) => {
    const queryClient = useQueryClient();
    const addToastMessage = useToastMessagesStore((state) => state.addToastMessage);
    const fileUploadRef = React.useRef<FileUpload>(null);
    const {mutateAsync: uploadFile, error: uploadFileError} = useUpdateUserGuide();

    const uploadHandler = async (event: MouseEvent) => {
        confirmPopup({
            target: event.target as HTMLElement,
            message: 'Please confirm to proceed moving forward.',
            icon: 'pi pi-exclamation-triangle',
            defaultFocus: 'accept',
            accept: async () => {
                await uploadFile({
                    file: fileUploadRef.current?.getFiles()[0],
                    is_static: true
                }, {
                    onSuccess: () => {
                        addToastMessage({
                            severity: 'success',
                            summary: 'User guide updated',
                            detail: 'User guide has been updated successfully',
                        });
                        queryClient.invalidateQueries({
                            queryKey: QueryKeys.misc._def
                        });
                        dialogProps.onHide();
                    },
                    onError: error => {
                        addToastMessage(CustomErrorMessage(error));
                    }
                });
            },
            reject: () => {

            }
        });


    };

    return (
        <>
            <UploadUserGuideModalStyle/>
            <Dialog
                {...dialogProps}
                className={clsx(dialogProps?.className, 'update-user-guide-modal')}
                header={'Upload User Guide'}
                draggable={false}
                blockScroll
            >
                <div className={'w-full'}>
                    <FileUpload
                        ref={fileUploadRef}
                        mode={fileUploadProps?.mode ?? 'advanced'}
                        multiple={fileUploadProps?.multiple ?? false}
                        cancelOptions={{style: {display: 'none'}}}
                        accept={fileUploadProps?.accept ?? '.pdf,.docx,.doc'}
                        emptyTemplate={fileUploadProps?.emptyTemplate ?? 'Drag and drop your file here'}
                        customUpload={fileUploadProps?.customUpload ?? true}
                        pt={{
                            uploadButton: {root: {onClick: uploadHandler}}
                        }}
                    />
                    <ConfirmPopup/>
                    <ErrorsBlock
                        className="block mt-2 p-error"
                        errorMessages={uploadFileError?.message}
                    />
                </div>
            </Dialog>
        </>
    );
};

export default UploadUserGuideModal;
